"use client";

import { useState } from "react";
import { signIn, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { clientLogger } from "@/lib/client-logger";
import { PrivySignInButton } from "@/components/auth/PrivySignInButton";

/**
 * Inline sign-in / sign-up panel that mirrors /auth/signin, for embedding where
 * we want auth without navigating away — e.g. the claim section on a clue page
 * after a valid tap. Primary path is Privy (email one-time code or wallet);
 * email + password is a backup behind a disclosure.
 *
 * Unlike the standalone page it does NOT redirect on success: a credentials
 * sign-in binds any anonymous IYK taps from this browser to the new session and
 * nudges SessionProvider to refetch, so the surrounding page re-renders as
 * authenticated and continues the claim in place. Privy logins are handled the
 * same way by the app-wide PrivyNextAuthBridge.
 */
export function InlineAuthPanel() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Email/password is a backup method, hidden behind a disclosure so the Privy
  // (email code + wallet) flow is the obvious default — same as /auth/signin.
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const { update } = useSession();
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const result = await signIn("credentials", {
        email: email.toLowerCase(),
        password,
        redirect: false,
      });

      if (result?.error) {
        setError("Invalid email or password");
      } else if (result?.ok) {
        // Bind anonymous taps from this browser to the now-authenticated user.
        try {
          await fetch("/api/auth/attach-taps", { method: "POST" });
        } catch (err) {
          clientLogger.warn("attach-taps call failed after inline signin", {
            error: err instanceof Error ? err.message : "Unknown",
          });
        }
        // Surface the new session in this tab without a full navigation. v4's
        // post-signIn refresh is unreliable, so nudge it the same way the Privy
        // bridge does: synthetic storage event, update(), router.refresh().
        if (typeof window !== "undefined") {
          window.dispatchEvent(new StorageEvent("storage"));
        }
        await update();
        router.refresh();
      } else {
        setError("Login failed. Please try again.");
      }
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-6 text-left">
      {/* Primary path: email one-time code or wallet connect via Privy. */}
      <PrivySignInButton caption="We'll email you a one-time code, or connect MetaMask." />

      {/* Backup path: email + password, hidden until requested. */}
      <div className="mt-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 border-t border-gray-300" />
          <span className="text-sm text-gray-500 whitespace-nowrap">or</span>
          <div className="flex-1 border-t border-gray-300" />
        </div>

        {!showPasswordForm ? (
          <button
            type="button"
            onClick={() => setShowPasswordForm(true)}
            className="mt-4 w-full text-center text-sm text-gray-600 hover:text-gray-900"
          >
            Sign in with a password instead
          </button>
        ) : (
          <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="inline-email">Email address</Label>
              <Input
                id="inline-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
                placeholder="Enter your email"
              />
            </div>
            <div>
              <Label htmlFor="inline-password">Password</Label>
              <Input
                id="inline-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
                placeholder="Enter your password"
              />
            </div>

            {error && <div className="text-red-600 text-sm text-center">{error}</div>}

            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? "Signing in..." : "Sign in"}
            </Button>

            <div className="text-center">
              <Link href="/auth/forgot-password" className="text-sm text-gray-600 hover:text-gray-900">
                Forgot your password?
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
